import { useEffect, useMemo } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Calendar,
  FileText,
  ListChecks,
  ChevronRight,
  Lock,
  Users,
  BookOpen,
  Archive,
  Building2,
  ShieldCheck,
  HandHeart,
} from "lucide-react";
import { GovernanceHealthContent } from "@/components/governance/GovernanceHealthContent";
import { GovernanceIntentIntake } from "@/components/governance/GovernanceIntentIntake";
import { GovernanceRecordContent } from "@/components/governance/GovernanceRecordContent";
import { TheRecordContent } from "@/components/governance/TheRecordContent";
import TaskCenterContent from "@/components/tasks/TaskCenterContent";
import { useMemberUna } from "@/hooks/useMemberUna";
import { useAuth } from "@/contexts/AuthContext";
import { useJourneyStatus } from "@/hooks/useJourneyStatus";
import { useUserRoles } from "@/hooks/useUserRoles";
import { supabase } from "@/integrations/supabase/client";


type TabKey = "tools" | "health" | "tasks" | "record";
const VALID_TABS: TabKey[] = ["tools", "health", "tasks", "record"];

type ToolCard = {
  key: string;
  title: string;
  description: string;
  href: string;
  icon: typeof Calendar;
  /** When set, the card only shows if this registry workflow row is active. */
  requiresWorkflowKey?: string;
};

const TOOL_CARDS: ToolCard[] = [
  {
    key: "meetings",
    title: "Meetings",
    description: "Record meetings and view certified minutes.",
    href: "/workflows?filter=meeting",
    icon: Calendar,
  },
  {
    key: "resolutions",
    title: "Resolutions",
    description: "Draft, adopt, and file resolutions.",
    href: "/workflows?filter=resolution",
    icon: FileText,
  },
  {
    key: "journal",
    title: "Journal Entries",
    description: "Log the ongoing governance journal.",
    href: "/workflows?filter=journal",
    icon: BookOpen,
  },
  {
    key: "give_into_stewardship",
    title: "Give Something Into Stewardship",
    description:
      "Give a vehicle, property, money, stocks, crypto, a business, or anything else into stewardship. The portal figures out the right way and prepares the papers.",
    href: "/workflows?workflow=give_into_stewardship",
    icon: HandHeart,
    requiresWorkflowKey: "give_into_stewardship",
  },
  {
    key: "form-a-company",
    title: "Form a Company",
    description: "Prepare the papers to start a Wyoming LLC (a limited liability company) owned by your Association.",
    href: "/governance/form-a-company",
    icon: Building2,
  },
  {
    key: "offices",
    title: "Offices",
    description: "Create, adopt, and record the offices of your Association.",
    href: "/governance/offices",
    icon: Building2,
  },
  {
    key: "certificates",
    title: "Officer's Certificates",
    description: "Prepare a short signed page (ready for a notary) that shows a bank or agency who your officers are.",
    href: "/governance/certificates",
    icon: FileText,
  },
  {
    key: "succession",
    title: "Succession",
    description: "Prepare the next steward of your family's Association.",
    href: "/succession/overview",
    icon: ShieldCheck,
  },
  {
    key: "tasks",
    title: "Tasks",
    description: "Open items and follow-ups assigned to you.",
    href: "/governance/tasks",
    icon: ListChecks,
  },
];

function LockedGate({ label }: { label: string }) {
  const navigate = useNavigate();
  return (
    <Card className="border-gold/30">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-gold" />
          <CardTitle>Finish your Journey to unlock {label}</CardTitle>
        </div>
        <CardDescription>
          Your onboarding Journey sets the foundation. Complete it and this section opens up.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={() => navigate("/journey")}>Continue My Journey</Button>
      </CardContent>
    </Card>
  );
}

const Governance = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Path-param tab: /governance, /governance/tools, /governance/health, etc.
  // Also accept legacy ?tab=X on bare /governance and redirect once, preserving hash.
  const pathTab = location.pathname.split("/")[2] as TabKey | undefined;
  const searchParams = new URLSearchParams(location.search);
  const legacyTab = searchParams.get("tab") as TabKey | null;

  useEffect(() => {
    if (!pathTab && legacyTab && VALID_TABS.includes(legacyTab)) {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      const search = next.toString();
      navigate(
        {
          pathname: `/governance/${legacyTab}`,
          search: search ? `?${search}` : "",
          hash: location.hash,
        },
        { replace: true },
      );
    }
  }, [pathTab, legacyTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const tab: TabKey = pathTab && VALID_TABS.includes(pathTab) ? pathTab : "tools";

  const { user } = useAuth();
  const { journeyCompleted: rawJourneyCompleted } = useJourneyStatus(user?.id);
  const { isAdmin } = useUserRoles(user?.id);
  const journeyCompleted = rawJourneyCompleted || isAdmin;
  const { unaId, unaName } = useMemberUna();


  // Count open health items for badge + banner
  const { data: openItemCount = 0 } = useQuery({
    queryKey: ["gov-open-count", unaId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("una_compliance_items")
        .select("*", { count: "exact", head: true })
        .eq("una_id", unaId!)
        .in("status", ["overdue", "due_soon"]);
      if (error) throw error;
      return count || 0;
    },
    enabled: !!unaId,
  });

  // Registry-gated tool cards: a card with requiresWorkflowKey only appears
  // once that workflow row is active. Activation alone makes it show up.
  const gatedKeys = useMemo(
    () => TOOL_CARDS.map((t) => t.requiresWorkflowKey).filter(Boolean) as string[],
    [],
  );
  const { data: activeWorkflowKeys = [] } = useQuery({
    queryKey: ["gov-tool-workflow-active", gatedKeys],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("workflows")
        .select("workflow_key")
        .in("workflow_key", gatedKeys)
        .eq("is_active", true);
      if (error) throw error;
      return (data || []).map((w: any) => w.workflow_key as string);
    },
    enabled: gatedKeys.length > 0,
  });

  const visibleToolCards = useMemo(
    () =>
      TOOL_CARDS.filter(
        (t) => !t.requiresWorkflowKey || activeWorkflowKeys.includes(t.requiresWorkflowKey),
      ),
    [activeWorkflowKeys],
  );


  const handleTabChange = (value: string) => {
    const search = location.search;
    const hash = location.hash;
    navigate(
      {
        pathname: value === "tools" ? "/governance" : `/governance/${value}`,
        search,
        hash,
      },
      { replace: true },
    );
  };

  const tabLabels = useMemo(
    () => ({
      tools: "Tools",
      health: "Health",
      tasks: "Tasks",
      record: "The Record",
    }),
    [],
  );

  return (
    <MainLayout>
      <div className="container max-w-5xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight title-rule">Governance</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Everything for running your UNA in one place.
          </p>
        </div>

        <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
          <div className="overflow-x-auto -mx-2 px-2">
            <TabsList className="w-full sm:w-auto grid grid-cols-4 sm:inline-flex">
              <TabsTrigger value="tools">{tabLabels.tools}</TabsTrigger>
              <TabsTrigger value="health" className="gap-1.5">
                {tabLabels.health}
                {openItemCount > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
                    {openItemCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="tasks">{tabLabels.tasks}</TabsTrigger>
              <TabsTrigger value="record">{tabLabels.record}</TabsTrigger>
            </TabsList>
          </div>

          {/* Tools */}
          <TabsContent value="tools" className="mt-4 sm:mt-6 space-y-6">
            {!journeyCompleted ? (
              <LockedGate label="your governance tools" />
            ) : (
              <>
                {isAdmin && !rawJourneyCompleted && (
                  <p className="text-xs text-muted-foreground italic">
                    Unlocked for admin view.
                  </p>
                )}
                {unaId && <GovernanceIntentIntake unaId={unaId} />}


                {openItemCount > 0 && (
                  <Alert className="border-amber-300 bg-amber-50">
                    <AlertDescription className="text-sm">
                      <button
                        className="underline underline-offset-2"
                        onClick={() => handleTabChange("health")}
                      >
                        {openItemCount} {openItemCount === 1 ? "item needs" : "items need"} your attention
                      </button>
                    </AlertDescription>
                  </Alert>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Take action</CardTitle>
                    <CardDescription>Start something new or continue a workflow.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {visibleToolCards.map((tool) => {
                        const Icon = tool.icon;
                        return (
                          <Link
                            key={tool.key}
                            to={tool.href}
                            className="group flex items-start gap-3 rounded-lg border p-4 hover:bg-muted/50 transition"
                          >
                            <div className="rounded-md bg-gold/15 p-2 shrink-0">
                              <Icon className="w-5 h-5 text-gold" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-medium">{tool.title}</span>
                                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">{tool.description}</p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2 text-sm">
                      <Button asChild variant="ghost" size="sm">
                        <Link to="/una-members">
                          <Users className="w-4 h-4 mr-1.5" />
                          Members
                        </Link>
                      </Button>
                      <Button asChild variant="ghost" size="sm">
                        <Link to="/governance-documents">
                          <FileText className="w-4 h-4 mr-1.5" />
                          Documents
                        </Link>
                      </Button>
                      <Button asChild variant="ghost" size="sm">
                        <Link to="/workflows">All workflows</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <div>
                  <h2 className="text-lg font-semibold mb-3">What happened</h2>
                  <GovernanceRecordContent unaId={unaId} />
                </div>
              </>
            )}
          </TabsContent>

          {/* Health */}
          <TabsContent value="health" className="mt-4 sm:mt-6">
            <GovernanceHealthContent />
          </TabsContent>

          {/* Tasks */}
          <TabsContent value="tasks" className="mt-4 sm:mt-6">
            {!journeyCompleted ? <LockedGate label="your task center" /> : <TaskCenterContent />}
          </TabsContent>

          {/* The Record */}
          <TabsContent value="record" className="mt-4 sm:mt-6">
            <TheRecordContent unaId={unaId} unaName={unaName} />
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default Governance;
